import { ArrowPathIcon, ArrowRightCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import EmptyState from '@/components/ui/EmptyState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useMigration } from './useMigration';

const PREPARATION_LABELS: Record<string, string> = {
  IDEAL: 'Idéale',
  REDUCED: 'Réduite',
  CONDENSED: 'Condensée',
  NONE: 'Sans compétition',
};

/**
 * PRD 12 §3 — the V1 → V2 mapping module, filled before the cutover.
 *
 * Mapping is by identity: the select writes a `plan.id`. Name matching is rejected because it fails
 * silently, and an athlete migrated onto the wrong plan never notices. A V1 plan left unmapped **blocks**
 * its athletes, deliberately — the gap is visible before the cutover rather than after.
 */
const MigrationPage = () => {
  const { mapping, isLoading, targets, values, set, dirty, save, dryRun, report, email, setEmail, apply, running, result } =
    useMigration();
  const target = email.trim();

  const counters = mapping?.counters;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Migration V1 → V2"
        description="Associez chaque plan V1 à son plan V2 avant la bascule. Un plan sans correspondance bloque ses athlètes."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => dryRun.mutate()} disabled={dryRun.isPending}>
              <ArrowPathIcon className="h-4 w-4" />
              {dryRun.isPending ? 'Vérification…' : 'Vérifier'}
            </Button>
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              Enregistrer
            </Button>
          </div>
        }
      />

      {counters && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card>
            <p className="text-sm text-gray-500 dark:text-gray-400">Plans associés</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">
              {counters.mappedPlans} <span className="text-base font-normal text-gray-500">sur {counters.totalPlans}</span>
            </p>
          </Card>
          <Card>
            <p className="text-sm text-gray-500 dark:text-gray-400">Athlètes couverts</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">
              {counters.coveredAthletes} <span className="text-base font-normal text-gray-500">sur {counters.totalAthletes}</span>
            </p>
          </Card>
        </div>
      )}

      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Input
              id="migration-email"
              label="Bascule"
              placeholder="Email d'un athlète — laisser vide pour migrer tous les athlètes"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <ConfirmDialog
            title="Lancer la migration"
            body={
              target
                ? `L'athlète ${target} sera basculé sur son plan V2. Ses jours d'entraînement lui seront demandés à la prochaine ouverture de l'app.`
                : 'Tous les athlètes V1 dont le plan est associé seront basculés sur V2. Les athlètes déjà migrés sont ignorés.'
            }
            confirm={target ? "Migrer l'athlète" : 'Migrer tous les athlètes'}
            destructive={false}
            onConfirm={() => apply.mutate()}
            trigger={
              <Button disabled={dirty || running || apply.isPending}>
                <ArrowRightCircleIcon className="h-4 w-4" />
                {running || apply.isPending ? 'Migration en cours…' : 'Migrer'}
              </Button>
            }
          />
        </div>
        {dirty && <p className="mt-2 text-xs text-gray-500">Enregistrez les correspondances avant de migrer.</p>}
      </Card>

      {result && (
        <Card>
          <h3 className="mb-4 font-semibold text-gray-900 dark:text-white">
            Bascule — {result.migrated} migré(s) · {result.skipped} ignoré(s) · {result.failed} échec(s)
          </h3>
          <ul className="space-y-2 text-sm">
            {result.outcomes
              .filter((o) => o.status === 'FAILED')
              .map((o) => (
                <li key={o.enrollmentId} className="rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                  <div className="font-mono text-xs text-gray-500">{o.userId}</div>
                  <div>
                    <Badge color="red">{o.outcome}</Badge> {o.reason}
                  </div>
                </li>
              ))}
          </ul>
        </Card>
      )}

      <Card>
        {isLoading ? (
          <EmptyState title="Chargement…" />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Plan V1</TH>
                <TH>Athlètes actifs</TH>
                <TH>Plan V2</TH>
                <TH>État</TH>
              </TR>
            </THead>
            <TBody>
              {(mapping?.plans ?? [])
                // Plans nobody is on stay visible but after the ones that matter.
                .slice()
                .sort((a, b) => b.activeAthletes - a.activeAthletes || a.name.localeCompare(b.name))
                .map((plan) => (
                  <TR key={plan.planId}>
                    <TD>
                      <div className="font-medium text-gray-900 dark:text-white">{plan.name}</div>
                      <div className="text-xs text-gray-500">
                        {plan.planId} · {plan.weekCount} sem. · {plan.keywords.join(', ')}
                      </div>
                    </TD>
                    <TD>{plan.activeAthletes}</TD>
                    <TD>
                      <Select value={values[plan.planId] ?? ''} onChange={(e) => set(plan.planId, e.target.value)}>
                        <option value="">— aucun —</option>
                        {targets.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.generable ? '' : '✕ '}
                            {t.displayName}
                          </option>
                        ))}
                      </Select>
                    </TD>
                    <TD>
                      {!plan.mapped ? (
                        plan.activeAthletes > 0 ? (
                          <Badge color="red">bloque {plan.activeAthletes} athlète(s)</Badge>
                        ) : (
                          <Badge color="gray">non associé</Badge>
                        )
                      ) : plan.v2Generable === false ? (
                        <Badge color="yellow">plan V2 non générable</Badge>
                      ) : (
                        <Badge color="green">associé</Badge>
                      )}
                    </TD>
                  </TR>
                ))}
            </TBody>
          </Table>
        )}
      </Card>

      {report && (
        <Card>
          <div className="mb-4 flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-brand-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Répétition à blanc — {report.athletes} athlète(s) · {report.ready} prêt(s) · {report.blocked} bloqué(s)
            </h3>
          </div>
          <p className="mb-4 text-xs text-gray-500">Rien n&apos;a été écrit : la vérification annule sa propre transaction.</p>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Répartition des préparations</h4>
              {Object.keys(report.preparations).length === 0 ? (
                <p className="text-sm text-gray-500">—</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {Object.entries(report.preparations).map(([kind, n]) => (
                    <li key={kind} className="flex justify-between">
                      <span>{PREPARATION_LABELS[kind] ?? kind}</span>
                      <span className="font-medium">{n}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Situations</h4>
              <ul className="space-y-1 text-sm">
                {Object.entries(report.outcomes).map(([kind, n]) => (
                  <li key={kind} className="flex justify-between">
                    <span>{kind}</span>
                    <span className="font-medium">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {report.notGenerable.length > 0 && (
            <div className="mt-6">
              <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Plans V2 associés mais non générables</h4>
              <ul className="space-y-1 text-sm">
                {report.notGenerable.map((p) => (
                  <li key={p.planId}>
                    {p.name} — {p.athletes} athlète(s)
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.failures.length > 0 && (
            <div className="mt-6">
              <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Athlètes dont la génération échouerait
              </h4>
              <ul className="space-y-2 text-sm">
                {report.failures.map((f) => (
                  <li key={f.userId} className="rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                    <div className="font-mono text-xs text-gray-500">{f.userId}</div>
                    <div>
                      <Badge color="red">{f.outcome}</Badge> {f.reason}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default MigrationPage;
